export interface PlexConnection {
  protocol: "http" | "https";
  address: string;
  port: number;
  local: 0 | 1;
  uri: string;
}

export interface PlexServer {
  name: string;
  product: string;
  connections: PlexConnection[];
}

export function rankPlexConnections(connections: PlexConnection[]): PlexConnection[] {
  // Ranking:
  // 1. HTTPS public (best — works from anywhere, no mixed content)
  // 2. HTTPS local
  // 3. HTTP public
  // 4. HTTP local (last resort — only works on HTTP app)
  return [...connections].sort((a, b) => {
    const score = (c: PlexConnection) =>
      (c.protocol === "https" ? 2 : 0) + (c.local === 0 ? 1 : 0);
    return score(b) - score(a);
  });
}

const PLEX_RESOURCES_URL = "https://plex.tv/api/v2/resources";

export const PlexAdapter = {
  async createPin(machineId: string): Promise<{ pinId: string; code: string }> {
    const res = await fetch("https://clients.plex.tv/api/v2/pins", {
      method: "POST",
      headers: { "X-Plex-Client-Identifier": machineId, "Content-Type": "application/json" },
      body: JSON.stringify({ type: "standard" }),
      cache: "no-store",
    });
    if (!res.ok) throw new Error("Plex PIN create failed");
    const data: { id: number; code: string } = await res.json();
    return { pinId: data.id.toString(), code: data.code };
  },

  async pollPin(pinId: string, machineId: string): Promise<{ authToken: string | null }> {
    const res = await fetch(`https://clients.plex.tv/api/v2/pins/${pinId}`, {
      headers: { "X-Plex-Client-Identifier": machineId },
      cache: "no-store",
    });
    if (!res.ok) throw new Error("Plex PIN poll failed");
    const data: { authToken?: string } = await res.json();
    return { authToken: data.authToken ?? null };
  },

  async getResources(authToken: string, machineId: string): Promise<{ servers: PlexServer[] }> {
    const res = await fetch(PLEX_RESOURCES_URL, {
      headers: { "X-Plex-Token": authToken, "X-Plex-Client-Identifier": machineId, Accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) throw new Error("Plex resources failed");
    interface ResourcesResp {
      MediaContainer: {
        Resource: Array<{
          name: string;
          product: string;
          Connection: PlexConnection[];
        }>;
      };
    }
    const data: ResourcesResp = await res.json();
    const servers: PlexServer[] = (data.MediaContainer.Resource ?? []).map((r) => ({
      name: r.name,
      product: r.product,
      connections: rankPlexConnections(r.Connection ?? []),
    }));
    return { servers };
  },

  async getLibrary(
    baseUrl: string,
    token: string,
    machineId: string,
    opts: { sectionId?: string } = {}
  ): Promise<{ items: Array<{ ratingKey: string; title: string; type: string; thumb: string | null }> }> {
    const sectionsUrl = `${baseUrl}/library/sections?X-Plex-Token=${encodeURIComponent(token)}`;
    const sectionsRes = await fetch(sectionsUrl, {
      headers: { "X-Plex-Client-Identifier": machineId, Accept: "application/json" },
      cache: "no-store",
    });
    if (!sectionsRes.ok) throw new Error("Plex sections failed");
    const sectionsData: { MediaContainer: { Directory: Array<{ key: string; type: string }> } } = await sectionsRes.json();
    const section = opts.sectionId
      ? sectionsData.MediaContainer.Directory.find((d) => d.key === opts.sectionId)
      : sectionsData.MediaContainer.Directory[0];
    if (!section) return { items: [] };

    const itemsRes = await fetch(`${baseUrl}/library/sections/${section.key}/all?X-Plex-Token=${encodeURIComponent(token)}`, {
      headers: { "X-Plex-Client-Identifier": machineId, Accept: "application/json" },
      cache: "no-store",
    });
    if (!itemsRes.ok) throw new Error("Plex items failed");
    const itemsData: { MediaContainer: { Metadata: Array<{ ratingKey: string; title: string; type: string; thumb: string | null }> } } = await itemsRes.json();
    return { items: itemsData.MediaContainer.Metadata ?? [] };
  },

  getStreamUrl(baseUrl: string, itemId: string, token: string, machineId: string): string {
    return `${baseUrl}/library/parts/?path=/library/metadata/${itemId}&X-Plex-Token=${encodeURIComponent(token)}&X-Plex-Client-Identifier=${machineId}`;
  },
};
